import React, { createContext, useState, useContext, useEffect } from 'react';
import { authAPI } from '../lib/api';
import axios from 'axios';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userPermissions, setUserPermissions] = useState({
    canCreateRapport: false,
    role: 'commercial',
    agence: null,
    permissions: {},
    scope: 'own',
    isSystem: false,
    roleName: null,
    canManageRoles: false,
  });

  // Récupérer les permissions de l'utilisateur depuis /users/me + /users/me/permissions
  const fetchUserPermissions = async (currentUser = null) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const [meRes, permRes] = await Promise.all([
        axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/users/me`, { headers: { 'Authorization': `Bearer ${token}` } }),
        axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/users/me/permissions`, { headers: { 'Authorization': `Bearer ${token}` } }).catch(() => ({ data: null })),
      ]);

      const userData = meRes.data;
      const permData = permRes.data || {};
      setUserPermissions({
        canCreateRapport: userData.can_create_rapport || false,
        role: userData.role || 'commercial',
        agence: userData.agence || null,
        permissions: permData.permissions || {},
        scope: permData.scope || 'own',
        isSystem: permData.is_system || false,
        roleName: permData.role_name || null,
        canManageRoles: permData.can_manage_roles || false,
      });

      // Mettre à jour l'utilisateur avec les nouvelles infos
      const baseUser = currentUser || JSON.parse(localStorage.getItem('user') || '{}');
      const updatedUser = { ...baseUser, ...userData };
      setUser(updatedUser);
      localStorage.setItem('user', JSON.stringify(updatedUser));
    } catch (error) {
      console.error('Erreur récupération permissions:', error);
    }
  };

  useEffect(() => {
    const token = localStorage.getItem('token');
    const savedUser = localStorage.getItem('user');
    
    if (token && savedUser) {
      setUser(JSON.parse(savedUser));
      // Récupérer les permissions à jour
      fetchUserPermissions();
    }
    setLoading(false);
  }, []);

  const login = async (credentials) => {
    const response = await authAPI.login(credentials);
    const { token, user: userData } = response.data;
    
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
    
    // Récupérer les permissions complètes après login
    setTimeout(() => fetchUserPermissions(userData), 100);
    
    return userData;
  };

  const register = async (userData) => {
    const response = await authAPI.register(userData);
    const { token, user: registeredUser } = response.data;
    
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(registeredUser));
    setUser(registeredUser);
    
    return registeredUser;
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    setUserPermissions({
      canCreateRapport: false, role: 'commercial', agence: null,
      permissions: {}, scope: 'own', isSystem: false, roleName: null, canManageRoles: false,
    });
    window.location.href = '/login';
  };

  // Helpers pour vérifier les permissions
  const isAdmin = () => userPermissions.role === 'admin' || userPermissions.role === 'energy_manager' || userPermissions.isSystem;
  const isResponsableAgence = () => userPermissions.role === 'responsable_agence';
  const canCreateRapport = () => userPermissions.canCreateRapport || userPermissions.role === 'admin' || userPermissions.role === 'energy_manager';

  // Permission granulaire (module + action) via les custom roles
  const hasPermission = (module, action) => {
    if (userPermissions.isSystem) return true;
    return Boolean(userPermissions.permissions?.[module]?.[action]);
  };

  return (
    <AuthContext.Provider value={{
      user,
      loading,
      login,
      register,
      logout,
      userPermissions,
      isAdmin,
      isResponsableAgence,
      canCreateRapport,
      hasPermission,
      fetchUserPermissions
    }}>
      {children}
    </AuthContext.Provider>
  );
};